export type AdminRole =
  | "super_admin"
  | "admin"
  | "coach"
  | "coach_manager"
  | "influencer"
  | "content_manager";

export type SystemRole = AdminRole;

export const tagGroupTypes = [
  "mood",
  "feeling",
  "shift",
  "cycle",
  "energy",
  "theme",
  "goal",
  "sensitivity",
  "spiritual",
  "custom",
] as const;
export type TagGroupType = (typeof tagGroupTypes)[number];

export interface TagGroup {
  id: string;
  name: string;
  type: TagGroupType;
  description: string | null;
  editable: boolean;
  approvalRequired: boolean;
  displayOrder: number;
  primaryLanguageCode: string;
  status: TaxonomyStatus;
  createdBy: string;
  createdByRole: "admin" | "coach";
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

// Taxonomy statuses (simple active/archived, no approval workflow)
export const taxonomyStatuses = ["active", "archived"] as const;
export type TaxonomyStatus = (typeof taxonomyStatuses)[number];

export interface TagOption {
  id: string;
  groupId: string;
  label: string;
  description: string | null;
  slug: string;
  displayOrder: number;
  primaryLanguageCode: string;
  status: TaxonomyStatus;
  parentTagOptionIds: string[];
  createdBy: string;
  createdByRole: "admin" | "coach";
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface TagGroupVariant {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
}

export interface TagOptionVariant {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedLabel: string;
  localizedDescription: string;
}

export const studioContentStatuses = [
  "draft",
  "submitted",
  // "scheduled",
  "transcoding",
  "published",
  "changes_requested",
  "archived",
  "failed",
  "outdated",
] as const;
export type StudioContentStatus = (typeof studioContentStatuses)[number];

export type CategoryStatus = "active" | "archived" | "pending";

// Category/Collection (library experiences)
export interface Category {
  id: string;
  type: "category";
  title: string;
  slug?: string;
  description: string | null;
  displayOrder: number;
  primaryLanguageCode: string;
  status: CategoryStatus;
  color: string | null;
  thumbnailUrl: string | null;

  ritualIds: string[];

  isFeatured: boolean;
  featuredOrder: number | null;

  createdBy: string;
  createdByRole: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  translations: CategoryTranslation[];
  contentCount?: number;
}

export interface CategoryTranslation {
  id: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string | null;
  localizedDescription: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Coach {
  id: string;
  userId: string;
  name: string;
  email: string;
  bio: string;
  photoUrl: string | null;
  expertise: string[];
  languages: string[];
  certifications: string[];
  coachType: CoachType;
  status: CoachStatus;
  statusReason: string | null;
  draftsCount: number;
  publishedCount: number;
  totalViews: number;
  totalCompletions: number;
  invitedAt: string | null;
  createdAt: string | null;
  lastActiveAt: string | null;
  lastSubmissionAt: string | null;
  pendingProfileChanges: any | null;
  pendingProfileSubmittedAt: string | null;
  socialLinks: SocialLink[];
  city?: string;
  state?: string;
  country?: string;
  location?: string;
  rating?: number;
  specialty?: string;
  contentCount?: number;
  totalSessions?: number;
}

export type SocialPlatform =
  | "Instagram"
  | "Website"
  | "LinkedIn"
  | "YouTube"
  | "Twitter"
  | "TikTok"
  | "Store"
  | "BookingCalendar"
  | "RetreatPage";

export interface SocialLink {
  platform: SocialPlatform;
  url: string;
}
export const coachTypes = ["coach", "verified_coach", "guest"] as const;
export type CoachType = (typeof coachTypes)[number];

export type CoachStatus = "active" | "suspended" | "deactivated";

export interface PaginatedCoachesResponse {
  coaches: Coach[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface InviteCoachData {
  name: string;
  email: string;
  coachType: CoachType;
}

export interface AffiliateUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
  username?: string;
  country?: string;
  language?: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  avatarUrl?: string;
  deletedAt?: string | null;
  influencerProfile?: {
    id: string;
    status: "ACTIVE" | "PENDING" | "SUSPENDED" | "REJECTED";
    avatarUrl?: string;
  };
  affiliate?: {
    id: string;
    affiliateCode: string;
    commissionRate: number;
    totalEarnings: number;
  };
  affiliateLink?: string;
}

export interface AffiliateTransaction {
  id: string;
  type: "commission" | "payout" | "adjustment" | "refund";
  status: "pending" | "completed" | "failed" | "reversed";
  amount: number;
  subscriptionAmount: number | null;
  commissionRate: number;
  currency: string;
  description: string | null;
  referredUser: {
    id: string;
    name: string;
    email: string;
  } | null;
  createdAt: string;
}

export interface StripeSubscriptionPlan {
  id: string;
  name: string;
  amount: number;
  currency: string;
  interval: string;
}

// Subscription plans for app users (string union)
export const subscriptionPlans = ["free", "trial", "paid"] as const;
export type SubscriptionPlan = (typeof subscriptionPlans)[number];

export interface AffiliateNote {
  id: string;
  content: string;
  createdAt: string;
  authorId: string;
  authorUsername: string;
}

export interface LanguageVariantDraft {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  localizedBody: string | null;
  thumbnailUrl: string | null;
}

export type TranslationStatus = "pending" | "in_progress" | "completed";

export interface BlogContent {
  id: string;

  categoryId?: string | null;
  subCategoryIds: string[];
  tagOptionIds: string[];

  status: StudioContentStatus;
  version: number;

  authorCoachId?: string | null;
  author?: {
    id: string;
    name: string | null;
    email: string;
  };

  createdBy: string;
  createdByName: string | null;

  publishedAt?: string | null;

  internalNotes?: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface BlogLanguageVariant {
  id: string;

  blogContentId: string;

  languageCode: string;
  isDefault: boolean;

  localizedTitle: string;
  localizedDescription?: string | null;
  localizedBody?: string | null;

  thumbnailUrl?: string | null;

  translationStatus: TranslationStatus;

  createdAt: string;
  updatedAt: string;
}

export const contentStatuses = [
  "draft",
  "submitted",
  "transcoding",
  "published",
  "archived",
  "failed",
  "changes_requested",
  "outdated",
] as const;
export type ContentStatus = (typeof contentStatuses)[number];

export interface AudioLanguageVariantDraft {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription?: string | null;
  audioFileUrl?: string | null;
  audioFileName?: string | null;
  thumbnailUrl?: string | null;
  durationSeconds?: number | null;
  coachId?: string | null;
}

export const RECIPE_DIET_TAGS = [
  "vegan",
  "vegetarian",
  "gluten_free",
  "dairy_free",
  "nut_free",
  "caffeine_free",
  "sugar_free",
  "keto",
  "paleo",
  "low_carb",
] as const;

export type RecipeDietTag = (typeof RECIPE_DIET_TAGS)[number];

export const RECIPE_DIET_TAG_LABELS: Record<RecipeDietTag, string> = {
  vegan: "Vegan",
  vegetarian: "Vegetarian",
  gluten_free: "Gluten-Free",
  dairy_free: "Dairy-Free",
  nut_free: "Nut-Free",
  caffeine_free: "Caffeine-Free",
  sugar_free: "Sugar-Free",
  keto: "Keto",
  paleo: "Paleo",
  low_carb: "Low-Carb",
};

export interface RecipeContent {
  id: string;
  rootContentId: string; // SAME across versions

  categoryId?: string | null;
  subCategoryIds: string[];
  tagOptionIds: string[];

  placementOverrides?: any;

  status: StudioContentStatus;
  version: number;

  authorCoachId?: string | null;
  author?: {
    id: string;
    name: string | null;
    email: string;
  };

  dietTags: RecipeDietTag[];
  servings: number | null;
  calories: number | null;

  createdBy: string;
  createdByRole: string;
  createdByName: string;

  submittedAt?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;

  reviewedBy?: string | null;
  reviewNote?: string | null;

  publishedAt?: string | null;
  archivedAt?: string | null;

  primaryLanguage: string;

  internalNotes?: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface RecipeLanguageVariant {
  id: string;

  recipeContentId: string;

  languageCode: string;
  isDefault: boolean;

  localizedTitle: string;
  localizedDescription?: string | null;
  aboutLongDescription: string | null;
  localizedIngredients: string[];
  localizedSteps: string[];
  userTips: string[];

  thumbnailUrl?: string | null;
  featuredImageUrl?: string | null;
  prepTimeMinutes?: number | null;
  cookTimeMinutes?: number | null;

  translationStatus: TranslationStatus;

  createdAt: string;
  updatedAt: string;
}

export interface AffirmationContent {
  id: string;
  rootContentId: string; // SAME across versions

  categoryId?: string | null;
  subCategoryIds: string[];
  tagOptionIds: string[];

  placementOverrides?: any;

  status: StudioContentStatus;
  version: number;

  authorCoachId?: string | null;
  author?: {
    id: string;
    name: string | null;
    email: string;
  };

  createdBy: string;
  createdByRole: string;
  createdByName: string;

  submittedAt?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;

  reviewedBy?: string | null;
  reviewNote?: string | null;

  publishedAt?: string | null;
  archivedAt?: string | null;

  primaryLanguage: string;

  internalNotes?: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface AffirmationLanguageVariantDraft {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  quoteText: string;
  authorName?: string | null;
  coachId?: string | null;
  featuredImageUrl: string | null;
  translationStatus?: TranslationStatus;
}

export interface ChallengeLanguageVariantDraft {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription?: string | null;
  audioContentId?: string | null;
  audioFileUrl?: string | null;
  audioFileName?: string | null;
  durationSeconds?: number | null;
  thumbnailUrl?: string | null;
  coachId?: string | null;
}

export interface AffirmationLanguageVariant {
  id: string;

  affirmationContentId: string;

  languageCode: string;
  isDefault: boolean;

  quoteText: string;

  authorName?: string | null;
  coachId?: string | null;
  featuredImageUrl?: string | null;

  translationStatus: TranslationStatus;

  createdAt: string;
  updatedAt: string;
}

export interface ChallengeContent {
  id: string;
  rootContentId: string; // SAME across versions

  categoryId?: string | null;
  subCategoryIds: string[];
  tagOptionIds: string[];

  placementOverrides?: any;

  status: StudioContentStatus;
  version: number;

  authorCoachId?: string | null;
  author?: {
    id: string;
    name: string | null;
    email: string;
  };

  createdBy: string;
  createdByRole: string;
  createdByName: string;

  submittedAt?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;

  reviewedBy?: string | null;
  reviewNote?: string | null;

  publishedAt?: string | null;
  archivedAt?: string | null;

  primaryLanguage: string;

  internalNotes?: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface ChallengeLanguageVariant {
  id: string;

  challengeContentId: string;

  languageCode: string;
  isDefault: boolean;

  localizedTitle: string;
  localizedDescription?: string | null;
  audioContentId?: string | null;
  audioFileUrl?: string | null;
  audioFileName?: string | null;
  durationSeconds?: number | null;
  thumbnailUrl?: string | null;
  coachId?: string | null;

  translationStatus: TranslationStatus;

  createdAt: string;
  updatedAt: string;
}

export type WorkbookBlockType = "prompt" | "journaling" | "challenge";

export interface WorkbookContent {
  id: string;
  categoryId: string | null;
  subCategoryIds: string[];
  tagOptionIds: string[];
  baseLanguage: string;
  createdBy: string;
  createdByRole: string;
  createdByName: string | null;
  latestVersion: number;
  status?: StudioContentStatus;
  version?: number;
  internalNotes?: string | null;
  createdAt: string;
  updatedAt: string;
  category?: Category | null;
  versions?: any[];
  blocks?: (WorkbookBlock & { variants: WorkbookBlockVariant[] })[];
}

export interface WorkbookBlock {
  id: string;
  workbookId: string;
  type: WorkbookBlockType;
  order: number;
  challengeId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkbookBlockVariant {
  id: string;
  blockId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedSectionTitle?: string | null;
  localizedPrompt: string | null;
  localizedInstructions: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkbookLanguageVariant {
  id: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedSubtitle?: string | null;
}

export interface WorkbookLanguageVariantDraft {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedSubtitle?: string | null;
}

export const albumTypes = ["audio", "video"] as const;
export type AlbumType = (typeof albumTypes)[number];

export const albumItemContentTypes = ["audio", "video"] as const;
export type AlbumItemContentType = (typeof albumItemContentTypes)[number];

export interface AlbumItem {
  id: string;
  albumId: string;
  contentId: string;
  contentType: AlbumItemContentType;
  contentTitle?: string;
  entityId?: string | null;
  displayOrder: number;
  addedAt: string;
  addedBy: string;
}

export interface StudioAlbum {
  id: string;
  name: string;
  description: string | null;
  albumType: AlbumType;
  categoryId: string | null;
  subCategoryId: string | null;
  tagOptionIds: string[];
  coverImage: string | null;
  primaryLanguageCode: string;
  status: TaxonomyStatus;
  createdBy: string;
  createdByRole: "admin" | "coach";
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  itemCount: number;
}

// Shared Content Intelligence Layer (used by all content types)
export interface ContentIntelligence {
  focusAreas: ConcentrationTopic[];
  goals: FeelingCalledTo[];
  emotionalStatesSupported: EmotionalState[];
  intendedEmotionalShift: {
    from: EmotionalState[];
    to: EmotionalState[];
  };
  timeRequirementMinutes: number;
  energyLevel: EnergyLevel;
  spiritualTone: SpiritualTone;
  sensitivityFlags: SensitivityFlag[];
}

// Energy levels
export const energyLevels = ["low", "medium", "high"] as const;
export type EnergyLevel = (typeof energyLevels)[number];

// Spiritual tones
export const spiritualTones = [
  "secular",
  "spiritual_light",
  "spiritual_deep",
  "faith_based",
] as const;
export type SpiritualTone = (typeof spiritualTones)[number];

// Base Content Studio Item (shared fields)
export interface StudioContentBase {
  id: string;
  title: string;
  description: string | null;
  status: StudioContentStatus;
  intelligence: ContentIntelligence;

  // Ownership
  ownerId: string;
  ownerType: "admin" | "coach";
  ownerName: string | null;

  // Dates
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  scheduledAt: string | null;
  publishedAt: string | null;
  archivedAt: string | null;

  // Review tracking
  reviewedBy: string | null;
  reviewedByName: string | null;
  reviewNote: string | null;

  // Version tracking
  version: number;

  // Internal notes
  internalNotes: string | null;
}

// Journey (multi-day program)
export interface Journey extends StudioContentBase {
  type: "journey";

  // Overview identity
  tagline: string | null;
  coreIntention: string | null;
  emotionalOutcomePromise: string | null;

  // Visual assets
  thumbnailUrl: string | null;

  // Classification
  categoryId: string | null;
  tagOptionIds: string[];

  // Coach credited on the app profile (null = none)
  authorCoachId?: string | null;

  // Language
  primaryLanguage: string;
  selectedLanguages: string[];

  // Program structure
  totalDays: number;
  estimatedDailyMinutes: number;
  difficultyLevel: JourneyDifficultyLevel;

  // Cycle phase relevance
  cyclePhaseRelevance: (
    "menstrual" | "follicular" | "ovulatory" | "luteal" | "any"
  )[];

  // Journey structure
  dayStructure: JourneyDay[];

  // Progression rules
  progressionType: "sequential" | "flexible";
  completionType: JourneyCompletionType;
  completionPercentage: number | null;

  // Related content
  workbookIds: string[];

  // Success criteria
  successMessage: string | null;
  completionBadgeUrl: string | null;
}

// Journey completion types
export const journeyCompletionTypes = [
  "all_required", // All required blocks must be completed
  "percentage", // X% of blocks must be completed
  "any_one", // At least one block completed
] as const;
export type JourneyCompletionType = (typeof journeyCompletionTypes)[number];

export interface JourneyDay {
  id: string;
  dayNumber: number;
  title: string;
  theme: string | null; // Day theme/intention
  description: string | null;

  // Day status
  status: JourneyDayStatus; // Draft or Ready

  // Unlock logic
  unlockType: DayUnlockType;
  unlockAfterHours: number | null; // For time_locked
  unlockDate: string | null; // For date_locked

  // Completion rules for this day
  completionType: JourneyCompletionType;
  completionPercentage: number | null;

  // Content blocks (replaces ritualIds)
  contentBlocks: JourneyContentBlock[];

  // Legacy support (ritualIds converted to contentBlocks)
  ritualIds: string[];
}

// Day unlock types
export const dayUnlockTypes = [
  "sequential", // Must complete previous day
  "time_locked", // Available after X hours from journey start
  "immediate", // Available immediately
  "date_locked", // Available on specific date
] as const;
export type DayUnlockType = (typeof dayUnlockTypes)[number];

// Content block within a journey day — stores reference IDs only
export interface JourneyContentBlock {
  id: string;
  order: number;
  contentId: string;
  contentType: JourneyBlockType;
  contentTitle: string | null;
  isRequired: boolean;
  estimatedMinutes: number;
}

// Content block types for journey days — reference-only content types
export const journeyBlockTypes = [
  "audio",
  "video",
  "blog",
  "workbook",
] as const;
export type JourneyBlockType = (typeof journeyBlockTypes)[number];

// Day status for journey days
export const journeyDayStatuses = ["draft", "ready"] as const;
export type JourneyDayStatus = (typeof journeyDayStatuses)[number];

// Emotional states supported
export const emotionalStates = [
  "anxious",
  "stressed",
  "overwhelmed",
  "sad",
  "lonely",
  "angry",
  "frustrated",
  "hopeful",
  "grateful",
  "peaceful",
  "joyful",
  "confident",
  "empowered",
  "curious",
  "reflective",
] as const;
export type EmotionalState = (typeof emotionalStates)[number];

// Sensitivity flags
export const sensitivityFlags = [
  "trauma",
  "grief",
  "body_image",
  "fertility",
  "loss",
  "addiction",
  "abuse",
  "mental_health",
] as const;
export type SensitivityFlag = (typeof sensitivityFlags)[number];

export const concentrationTopics = [
  "Self-Love & Acceptance",
  "Radiant Confidence",
  "Expanding Joy & Gratitude",
  "Living in Alignment With Your Values",
  "Cultivating Inner Peace & Balance",
  "Healing After Heartbreak",
  "Calling In Love",
  "Mastering Relationships",
  "Professional Growth & Confidence",
  "Clarity & Direction for Your Goals",
  "Developing Leadership & Influence",
  "Abundance & Financial Flow",
  "Cultivating Daily Rituals for Success",
  "Living in Sync With Your Hormonal Cycle",
  "Aligning With Lunar Energy & Moon Phases",
  "Using Astrology for Self-Discovery",
  "Emotional Resilience & Strength",
  "Releasing Stress & Restoring Calm",
] as const;
export type ConcentrationTopic = (typeof concentrationTopics)[number];

export const feelingCalledToOptions = [
  "To feel more confident and worthy",
  "To call in aligned love",
  "To create more abundance in my life",
  "To heal emotional wounds or heartbreak",
  "To reconnect with my intuition",
  "To feel more peace and calm in my day",
  "To build habits that support my future self",
  "To feel energized and inspired again",
  "To work with my hormonal cycle for balance and flow",
  "To embrace change and step into my next chapter",
  "To explore and align with my astrology",
] as const;
export type FeelingCalledTo = (typeof feelingCalledToOptions)[number];

export type StudioContentType =
  | "audio"
  | "video"
  | "blog"
  | "recipe"
  | "workbook"
  | "affirmation"
  | "journey"
  | "ritual"
  | "astrology";

export const CONTENT_TYPE_LABELS: Record<StudioContentType, string> = {
  audio: "Audio",
  video: "Video",
  blog: "Blog",
  recipe: "Recipe",
  workbook: "Workbook",
  affirmation: "Affirmation",
  journey: "Journey",
  ritual: "Ritual",
  astrology: "Astrology",
};

export interface DashboardStats {
  totalActiveUsers: number;
  totalActiveUsersTrend: number | null;
  averageEngagement: number;
  averageEngagementTrend: number | null;
  trialsEnding?: number;
  membersNeedingSupport?: number;
  myDrafts?: number;
  myPublished?: number;
  pendingApproval?: number;
  totalViews?: number;
  followers?: number;
  referralSignups?: number;
  paidReferrals?: number;
  commissionEarned?: number;
}

export interface ActivityItem {
  id: string;
  type:
    | "content_published"
    | "coach_added"
    | "system_alert"
    | "content_review"
    | "content_rejected";
  title: string;
  description: string;
  timestamp: string;
  actorRole?: SystemRole;
}

// Ritual & Journey Flow metrics
export interface RitualFlowData {
  dailyEngagement: { day: string; rituals: number; journeys: number }[];
  ritualTypeBalance: { type: string; count: number; percentage: number }[];
  ritualCompletionEnergy: number;
  journeyMomentum: number;
  averageRitualDepth: number;
}

// A single piece of coach-owned content surfaced on the coach dashboard.
export interface CoachContentItem {
  id: string;
  contentType: string;
  title: string;
  status: string;
  updatedAt: string;
  editUrl: string;
}

// Care & Support Queue items
export interface CareQueueItem {
  id: string;
  type: "needs_support" | "trial_expiring" | "stuck_journey" | "pending_review";
  title: string;
  subtitle: string;
  urgency: "low" | "medium" | "high";
  actionLabel: string;
  actionRoute: string;
}

// Subscription Health overview
export interface SubscriptionHealthData {
  activeMemberships: number;
  activeMembershipsTrend: number | null;
  trialBeginnings: number;
  careAtRisk: number;
  returningMembers: number;
  monthlySupportValue: number;
  monthlySupportValueTrend: number | null;
  accessIssues: number;
  membershipTrend: { month: string; active: number; trial: number }[];
}

// Revenue Data for dashboard chart
export interface RevenueData {
  dataPoints: { date: string; revenue: number; subscriptions: number }[];
  totalRevenue: number;
  totalSubscriptions: number;
  avgDailyRevenue: number;
  trend: number | null;
  period: string;
  granularity: "day" | "week" | "month";
}

// Revenue & Channels - per-channel breakdown for the dashboard
export interface RevenueChannelDataPoint {
  date: string;
  monthly: number;
  yearly: number;
}
export interface RevenueChannelSummary {
  dataPoints: RevenueChannelDataPoint[];
  totalMonthly: number;
  totalYearly: number;
  total: number;
  trend: number | null;
}
export interface CommissionsDataPoint {
  date: string;
  amount: number;
  affiliateRevenue: number;
  percentOfAffiliate: number;
}
export interface CommissionsSummary {
  dataPoints: CommissionsDataPoint[];
  total: number;
  avgPercent: number;
  trend: number | null;
  lifetimeTotal: number;
  lifetimeCount: number;
}
export interface RevenueChannelsData {
  period: string;
  granularity: "day" | "week" | "month";
  mobile: RevenueChannelSummary;
  affiliate: RevenueChannelSummary;
  commissions: CommissionsSummary;
}

// === Dashboard mock-replacement types ===

export type DashboardContentType =
  | "audio"
  | "video"
  | "blog"
  | "recipe"
  | "affirmation"
  | "challenge"
  | "workbook"
  | "journey";

export interface PendingReviewItem {
  id: string;
  title: string;
  type: DashboardContentType;
  coachName: string | null;
  submittedAt: string;
  status: "pending_review" | "submitted";
}

export interface CoachActivityItem {
  id: string;
  coachId: string;
  coachName: string;
  action: string;
  timestamp: string;
  status: "active" | "onboarding" | "needs_attention";
}

export interface ContentCoverageItem {
  type: string;
  coverage: number;
  color?: string;
}

export interface RecentlyPublishedItem {
  id: string;
  title: string;
  type: string;
  publishedAt: string;
}

export interface ContentPerformanceData {
  coverage: ContentCoverageItem[];
  recentlyPublished: RecentlyPublishedItem[];
}

export interface CoachEngagementSignals {
  qualityScore: string | null;
  responseTime: string | null;
  activeRate: number | null;
}

export interface CoachNeedingAttentionItem {
  id: string;
  name: string;
  reason: string;
  lastActiveAt: string | null;
}

export interface CoachQualityData {
  engagementSignals: CoachEngagementSignals;
  coachesNeedingAttention: CoachNeedingAttentionItem[];
}

export interface ContentManagerStats {
  publishedThisWeek: number;
  coachSubmissions: number;
  contentGaps: number;
}

export interface CoachManagerStats {
  activeCoaches: number;
  onboardingCoaches: number;
  pendingCoachSubmissions: number;
  coachesNeedingAttention: number;
}

export interface ManagerStatsData {
  contentManager?: ContentManagerStats;
  coachManager?: CoachManagerStats;
}

// Source of the action
export type AuditActionSource =
  "manual" | "system" | "automated" | "api" | "scheduled";

export type AuditActorType =
  | "super_admin"
  | "admin"
  | "coach_manager"
  | "coach"
  | "system"
  | "scheduled"
  | "api";

export interface AuditLogEntry {
  id: string;
  actorType: AuditActorType;
  actorId: string;
  actorUsername: string;
  actorName?: string;
  actorRole: SystemRole | "system" | "scheduled" | "api";
  actionType: AuditLogActionType;
  entityType:
    | "user"
    | "coach"
    | "content"
    | "subscription"
    | "failed_payment"
    | "invitation"
    | "category"
    | "tag"
    | "affiliate"
    | "partner_request"
    | "system"
    | "notification"
    | "gdpr";
  entityId: string;
  entitySubtype?: string;
  entityLabel?: string;
  description: string;
  reason?: string;
  source: AuditActionSource;
  metadata?: Record<string, unknown>;
  privacyMetadata?: AuditPrivacyMetadata;
  timestamp: string;
  ipAddress?: string;
  // Legacy fields for backwards compatibility
  adminId?: string;
  adminUsername?: string;
  adminRole?: SystemRole;
}

// Privacy metadata for sensitive actions
export interface AuditPrivacyMetadata {
  containsSensitiveData: boolean;
  dataCategories?: string[];
  redactedFields?: string[];
  privacyJustification?: string;
}

// Audit Log for tracking all sensitive actions (50+ action types)
export const auditLogActionTypes = [
  // App user actions
  "app_user_force_logout",
  "app_user_note_added",
  "app_user_trial_granted",
  // Coach actions
  "coach_invited",
  "coach_activated",
  "coach_updated",
  "coach_deleted",
  "coach_restored",
  // Content actions
  "content_created",
  "content_updated",
  "content_submitted",
  "content_changes_requested",
  "content_approved",
  "content_published",
  "content_withdrawn",
  "content_archived",
  "content_restored",
  "content_deleted",
  "content_version_created",
  // Subscription actions
  "subscription_cancelled",
  "subscription_paused",
  "subscription_resumed",
  "subscription_trial_extended",
  "subscription_complimentary_granted",
  "subscription_webhook_event",
  "failed_payment_status_updated",
  // Admin user / internal-user actions
  "admin_user_role_updated",
  "admin_user_role_removed",
  "admin_user_suspended",
  "admin_user_restored",
  "admin_user_deleted",
  "admin_user_email_changed",
  "admin_user_password_changed",
  "admin_invitation_created",
  "admin_invitation_resent",
  "admin_invitation_cancelled",
  "admin_invitation_accepted",
  // Taxonomy actions
  "category_created",
  "category_updated",
  "category_deleted",
  "category_archived",
  "category_restored",
  "category_translation_deleted",
  "tag_created",
  "tag_updated",
  "tag_deleted",
  // Affiliate / partner actions
  "affiliate_created",
  "affiliate_updated",
  "affiliate_suspended",
  "affiliate_restored",
  "affiliate_deleted",
  "partner_request_approved",
  "partner_request_rejected",
] as const;

export type AuditLogActionType = (typeof auditLogActionTypes)[number];

export interface CoachSocialLinks {
  instagram?: string;
  linkedin?: string;
  twitter?: string;
  youtube?: string;
  website?: string;
  tiktok?: string;
  store?: string;
  bookingCalendar?: string;
  retreatPage?: string;
}

export interface ContentItem {
  id: string;
  title: string;
  type: string;
  coachName: string | null;
  submittedAt: string;
  status: string;
}

// Subscription dashboard stats
export interface SubscriptionStats {
  activeSubscriptions: number;
  monthlyRevenue: number;
  activeTrials: number;
  trialsExpiringSoon: number;
  pausedSubscriptions: number;
  churnRate: number;
}

// Trials expiring soon item
export interface TrialExpiringSoon {
  id: string;
  appUserId: string;
  userName: string | null;
  maskedEmail: string | null;
  plan: ExtendedSubscriptionPlan;
  trialEndDate: string;
  engagementState: EngagementState;
  daysRemaining: number;
}

// Account engagement states (system-driven)
export const engagementStates = [
  "active",
  "at_risk",
  "inactive",
  "dormant",
  "churned",
] as const;
export type EngagementState = (typeof engagementStates)[number];

// Extended subscription plans for admin portal
export const extendedSubscriptionPlans = [
  "free",
  "trial",
  "active",
  "paused",
  "cancelled",
  "expired",
  "lifetime",
] as const;
export type ExtendedSubscriptionPlan =
  (typeof extendedSubscriptionPlans)[number];

// Subscription record for detailed management
export interface Subscription {
  id: string;
  appUserId: string;
  userName: string | null;
  maskedEmail: string | null;
  plan: ExtendedSubscriptionPlan;
  status: SubscriptionStatus;
  engagementState: EngagementState;
  startDate: string;
  trialEndDate: string | null;
  currentPeriodEnd: string | null;
  lastActiveAt: string | null;
  monthlyAmount: number | null;
  currency: string;
  createdAt: string;
}

// Subscription statuses
export const subscriptionStatuses = [
  "active",
  "paused",
  "cancelled",
  "expired",
  "none",
] as const;
export type SubscriptionStatus = (typeof subscriptionStatuses)[number];

// Subscription action history
export interface SubscriptionHistoryItem {
  id: string;
  subscriptionId: string;
  action: SubscriptionActionType;
  performedBy: string;
  performedByRole: SystemRole;
  reason: string;
  previousValue: string | null;
  newValue: string | null;
  createdAt: string;
}

// Subscription action types for audit
export const subscriptionActionTypes = [
  "trial_extended",
  "complimentary_granted",
  "subscription_paused",
  "subscription_resumed",
  "subscription_cancelled",
] as const;
export type SubscriptionActionType = (typeof subscriptionActionTypes)[number];

// Activity item for timeline
export interface ActivityItem {
  id: string;
  type:
    | "content_published"
    | "coach_added"
    | "system_alert"
    | "content_review"
    | "content_rejected";
  title: string;
  description: string;
  timestamp: string;
  actorRole?: SystemRole;
}

// Mobile App User (distinct from admin users)
export interface AppUser {
  id: string;
  name: string | null;
  email: string | null;
  username: string | null;
  phone: string | null;
  avatarUrl: string | null;
  country: string | null;
  timezone: string;
  createdAt: string;
  updatedAt: string;
  lastActiveAt: string | null;
  isSuspended: boolean;
  suspendedReason: string | null;
  suspendedAt: string | null;
  language: string;
  concentrationTopics: ConcentrationTopic[];
  feelingCalledTo: FeelingCalledTo[];
  contentInterests: ContentInterest[];
  preferredDuration: string[];
  religion: Religion | null;
  birthDate: string | null;
  birthCity: string | null;
  birthTime: string | null;
  relationshipStatus: RelationshipStatus | null;
  hasChildren: ChildrenOption | null;
  childrenCount: ChildrenCount | null;
  childrenAgeGroups: string[];
  suggestionTime: string | null;
  ageGroup: AgeGroup | null;

  // Subscription
  plan: SubscriptionPlan;
  trialEndDate: string | null;
  subscriptionStatus: SubscriptionStatus;

  // Engagement state (system-driven)
  engagementState: EngagementState;

  // Engagement summary (aggregated, privacy-safe)
  totalSessionsCompleted: number;
  completedJourneys: number;
  qualifiedDays: number;
  currentStreakDays: number;
  currentStreakActive: boolean;

  moodDataAvailable: boolean;
  cycleTrackingConnected: boolean;
  sleepDataConnected: boolean;
  activityDataConnected: boolean;
  nutritionDataPresent: boolean;
  weightDataPresent: boolean;
  overallWellnessTrend: WellnessTrend;

  // Connected apps
  appleHealthConnected: boolean;
  googleFitConnected: boolean;
  lastSyncTimestamp: string | null;
  syncErrorState: string | null;

  // Account status

  // Spiritual/religious background

  // Preferred session length
  availableTime: AvailableTime | null;

  // Birth information for astrology features

  birthCountry: string | null;

  // Children information

  childrenAges: ChildrenAgeRange[];

  // Preferred time for personalized suggestions
  personalSuggestionsTime: string | null;
}

// Wellness trend indicators (qualitative only - privacy safe)
export const wellnessTrends = ["improving", "stable", "declining"] as const;
export type WellnessTrend = (typeof wellnessTrends)[number];

// App user list filters
export interface AppUserFilters {
  search?: string;
  engagementState?: EngagementState;
  plan?: SubscriptionPlan;
  subscriptionStatus?: SubscriptionStatus;
  country?: string;
  language?: string;
  ageGroup?: AgeGroup;
  lastActiveBefore?: string;
  lastActiveAfter?: string;
}

// Masked app user for operations view
export interface MaskedAppUser {
  id: string;
  name: string | null;
  maskedEmail: string | null;
  maskedPhone: string | null;
  country: string | null;
  language: string;
  timezone: string;
  ageGroup: AgeGroup | null;
  plan: SubscriptionPlan;
  trialEndDate: string | null;
  engagementState: EngagementState;
  createdAt: string;
  lastActiveAt: string | null;
}

// Targeting filter (used in segment-based notifications)
export interface NotificationTargetFilter {
  subscriptionStatus?: SubscriptionStatus[];
  appUsageState?: EngagementState[];
  journeyId?: string;
  language?: string[];
  region?: string[];
  timezone?: string[];
  country?: string[];
  age?: string[];
  concentration?: string[];
  religion?: string[];
  relationshipStatus?: string[];
  children?: string[];
}

// Notification interface
export interface Notification {
  id: string;
  category: NotificationCategory;
  title: string;
  body: string;
  channels: NotificationChannel[];
  scope: NotificationScope;

  // Targeting
  targetUserId?: string | null; // For individual scope
  targetFilter?: NotificationTargetFilter | null; // For segment scope
  estimatedRecipients?: number | null; // Calculated count

  // Scheduling
  status: NotificationStatus;
  scheduledAt?: string | null;
  sentAt?: string | null;

  // Repeat settings
  repeatFrequency?: RepeatFrequency;
  repeatEndDate?: string | null; // When to stop repeating
  lastSentAt?: string | null; // Track last send for recurring

  // Sender info
  createdBy: string;
  createdByUsername: string;
  createdByRole: SystemRole;
  createdAt: string;

  // Metadata
  messageRefId: string; // Reference ID for audit (no raw text stored in logs)
  priority: "low" | "normal" | "high" | "critical";

  // Delivery stats
  deliveredCount?: number;
  failedCount?: number;
  openedCount?: number;
  failedRecipients?: Array<{
    userId: string;
    channel: NotificationChannel;
    errorMessage: string;
    retryCount: number;
    resolvedAt?: string | null;
  }>;
  retryCount?: number;
  nextRetryAt?: string | null;
  lastErrorMessage?: string | null;
  parentNotificationId?: string | null;
}

export interface NotificationsListResponse {
  items: Notification[];
  total: number;
  page: number;
  pageSize: number;
  counts: {
    draft: number;
    scheduled: number;
    sent: number;
    failed: number;
    cancelled: number;
  };
}

// Notification audit log entry (immutable). Shape mirrors the server's
// NotificationAuditEntry Prisma model in `server/prisma/schema.prisma`.
export interface NotificationAuditEntry {
  id: string;
  notificationId: string;
  action:
    | "created"
    | "scheduled"
    | "sent"
    | "cancelled"
    | "deleted"
    | "failed"
    | "retry";
  actorId: string;
  scope: NotificationScope;
  channels: NotificationChannel[];
  recipientCount?: number | null;
  successCount?: number | null;
  failureCount?: number | null;
  errorSummary?: string | null;
  createdAt: string;
}

// Coach notification (subset for content-related notifications to coaches)
export interface CoachNotification {
  id: string;
  coachId: string;
  type:
    | "content_approved"
    | "content_rejected"
    | "content_changes_requested"
    | "performance_summary";
  title: string;
  body: string;
  contentId?: string | null;
  contentTitle?: string | null;
  createdAt: string;
  readAt?: string | null;
  createdBy: string;
  createdByRole: SystemRole;
}

// Rate limiting configuration
export interface NotificationRateLimitConfig {
  maxPerUserPerHour: number;
  maxPerUserPerDay: number;
  maxBulkPerHour: number;
  cooldownMinutes: number;
}

// Notification categories
export const notificationCategories = [
  "system", // Security alerts, legal notices, outages, critical fixes
  "account_billing", // Trial ending, subscription changes, payment issues
  "content", // New content releases, journey announcements, coach approvals
] as const;
export type NotificationCategory = (typeof notificationCategories)[number];

// Notification channels
// in_app channel deferred to a future iteration — UI hides it; type kept compact
export const notificationChannels = ["push", "email"] as const;
export type NotificationChannel = (typeof notificationChannels)[number];

// Notification target scope
export const notificationScopes = ["individual", "segment", "all"] as const;
export type NotificationScope = (typeof notificationScopes)[number];

// Notification status
export const notificationStatuses = [
  "draft",
  "scheduled",
  "sent",
  "failed",
  "cancelled",
] as const;
export type NotificationStatus = (typeof notificationStatuses)[number];

// Insert notification schema (simplified for creation)
export interface InsertNotification {
  category: NotificationCategory;
  title: string;
  body: string;
  channels: NotificationChannel[];
  scope: NotificationScope;
  targetUserId?: string | null;
  targetFilter?: NotificationTargetFilter | null;
  scheduledAt?: string | null;
  createdBy: string;
  createdByUsername: string;
  createdByRole: SystemRole;
  priority?: "low" | "normal" | "high" | "critical";
}

// Support note (internal, never visible to users)
export interface SupportNote {
  id: string;
  appUserId: string;
  authorId: string;
  authorUsername: string;
  authorRole: SystemRole;
  content: string;
  createdAt: string;
}

// Age groups (privacy-safe demographic buckets)
export const ageGroups = ["18-24", "25-34", "35-44", "45-54", "55+"] as const;
export type AgeGroup = (typeof ageGroups)[number];

// Failed payment record
export interface FailedPayment {
  id: string;
  appUserId: string;
  userName: string | null;
  maskedEmail: string | null;
  amount: number;
  currency: string;
  reason: FailedPaymentReason;
  reasonDisplay: string;
  status: FailedPaymentStatus;
  attemptCount: number;
  lastAttemptAt: string;
  nextRetryAt: string | null;
  createdAt: string;
}

// Failed payment reasons
export const failedPaymentReasons = [
  "card_declined",
  "insufficient_funds",
  "expired_card",
  "invalid_card",
  "processing_error",
  "authentication_required",
] as const;
export type FailedPaymentReason = (typeof failedPaymentReasons)[number];

// Failed payment status
export const failedPaymentStatuses = [
  "pending",
  "resolved",
  "escalated",
] as const;
export type FailedPaymentStatus = (typeof failedPaymentStatuses)[number];

// Repeat frequency options
export const repeatFrequencies = [
  "none",
  "daily",
  "weekly",
  "monthly",
] as const;
export type RepeatFrequency = (typeof repeatFrequencies)[number];

// Insert notification schema (simplified for creation)
export interface InsertNotification {
  category: NotificationCategory;
  title: string;
  body: string;
  channels: NotificationChannel[];
  scope: NotificationScope;
  targetUserId?: string | null;
  targetFilter?: NotificationTargetFilter | null;
  scheduledAt?: string | null;
  createdBy: string;
  createdByUsername: string;
  createdByRole: SystemRole;
  priority?: "low" | "normal" | "high" | "critical";
}

// Workbook (guided reflection system)
export interface Workbook extends StudioContentBase {
  type: "workbook";

  // Prompts/questions
  prompts: WorkbookPrompt[];

  // Linked journey (optional)
  linkedJourneyId: string | null;
  linkedJourneyDay: number | null;
}

export interface WorkbookPrompt {
  id: string;
  order: number;
  promptText: string;
  promptType: "free_text" | "rating" | "multiple_choice" | "reflection";
  options: string[] | null; // For multiple choice
  isRequired: boolean;
}

// Journey difficulty levels
export const journeyDifficultyLevels = [
  "beginner",
  "intermediate",
  "advanced",
  "deep_work",
] as const;
export type JourneyDifficultyLevel = (typeof journeyDifficultyLevels)[number];

// Children age ranges
export const childrenAgeRanges = [
  "0-2",
  "2-5",
  "6-12",
  "13-18",
  "19+",
] as const;
export type ChildrenAgeRange = (typeof childrenAgeRanges)[number];

// Religion
export const religionOptions = [
  "Christianity",
  "Hindu",
  "Buddhist",
  "Islam",
  "Judaism",
  "Catholic",
  "General Spirituality",
  "Other",
  "None",
] as const;
export type Religion = (typeof religionOptions)[number];

// Relationship Status
export const relationshipStatuses = [
  "Single, looking for fun",
  "Married",
  "In a relationship",
  "Single, dating without intention",
  "Single, looking for a relationship",
  "Single, not looking for a relationship",
  "Healing from past relationship",
] as const;
export type RelationshipStatus = (typeof relationshipStatuses)[number];

// Children options
export const childrenOptions = ["Yes", "No", "Prefer Not to Say"] as const;
export type ChildrenOption = (typeof childrenOptions)[number];

// Children count (if Yes)
export const childrenCounts = ["1", "2", "3", "3 or more"] as const;
export type ChildrenCount = (typeof childrenCounts)[number];

// Available Time on App
export const availableTimeOptions = [
  "Very Short (1-3 minutes)",
  "Short (3-10 minutes)",
  "Medium (10-20 minutes)",
  "Long (20 minutes - 1 hour)",
  "All lengths work for me",
  "I'll choose what I need each time",
] as const;
export type AvailableTime = (typeof availableTimeOptions)[number];

// Content Interests
export const contentInterests = [
  "Invigorations",
  "Affirmations",
  "Manifestations Journeys",
  "Breathwork & Nervous System Reset",
  "Educational Journeys",
  "Guided Meditations",
  "Astrology & Cosmic Guidance",
  "Movement & Workout Motivation",
  "Somatic Practices",
  "Cycle & Hormone Support",
  "Journaling & Reflection Prompts",
  "Daily Rituals",
] as const;
export type ContentInterest = (typeof contentInterests)[number];

// ----- Library categories -----
export const libraryCategoryStatuses = ["draft", "live", "archived"] as const;
export type LibraryCategoryStatus = (typeof libraryCategoryStatuses)[number];

export interface LibraryCategoryContentItem {
  contentId: string;
  order: number;
}

// ----- Coach extras -----
export interface CoachProfileChangeRequest {
  coachId: string;
  changes: Partial<Coach>;
  submittedAt: string;
  status: "pending" | "approved" | "rejected";
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

export interface CoachFilters {
  search?: string;
  coachType?: CoachType;
  status?: CoachStatus;
  language?: string;
}

// ----- System & dashboard -----
export interface SystemHealthMetric {
  id: string;
  name: string;
  status: "healthy" | "warning" | "attention";
  value: number;
  description: string;
  lastUpdated: string;
}

export interface WellnessEcosystemSignal {
  id: string;
  label: string;
  value: string;
  interpretation: string;
  trend?: "up" | "down" | "stable";
  variant: "rose" | "sage" | "lavender" | "sand";
}

export interface NovaReadinessData {
  overallReadiness: "fully_nourished" | "partial" | "gaps";
  emotionalStateCoverage: { state: string; coverage: number }[];
  recommendationAvailability: number;
  underSupportedStates: string[];
  contentGaps: { type: string; gap: string }[];
}

export interface UserOverview {
  id: string;
  accountStatus: "active" | "inactive" | "paused";
  dataAvailable: boolean;
  engagementLevel: "low" | "medium" | "high";
  journeyStatus: "improving" | "stable" | "needs_attention";
  joinDate: string;
  maskedEmail?: string;
  subscriptionStatus?: "trial" | "active" | "expired" | "cancelled";
  supportNotes?: string;
}

export interface EngagementTrend {
  date: string;
  activeUsers: number;
  completedRituals: number;
}

// ----- Notifications (system-level) -----
export const notificationTargets = [
  "all_users",
  "active_users",
  "inactive_users",
  "trial_users",
] as const;
export type NotificationTarget = (typeof notificationTargets)[number];

export interface SystemNotification {
  id: string;
  title: string;
  message: string;
  target: NotificationTarget;
  sentBy: string;
  sentByRole: SystemRole;
  sentAt: string;
}

// ----- App user audit -----
export const appUserActionTypes = [
  "app_user_viewed",
  "app_user_impersonated",
  "app_user_force_logout",
  "app_user_password_reset",
  "app_user_trial_granted",
  "app_user_refunded",
  "app_user_suspended",
  "app_user_restored",
  "app_user_deleted_gdpr",
  "app_user_subscription_updated",
  "app_user_note_added",
] as const;
export type AppUserActionType = (typeof appUserActionTypes)[number];

export interface UserActionHistoryItem {
  id: string;
  actionType: AuditLogActionType;
  description: string;
  timestamp: string;
  actorType: AuditActorType;
  actorUsername?: string;
  source: AuditActionSource;
}

// ----- Notification targeting (privacy criteria) -----
export const allowedTargetingCriteria = [
  "subscription_status",
  "app_usage_state",
  "journey_enrollment",
  "language",
  "region",
  "timezone",
  "country",
  "age",
  "concentration",
  "religion",
  "relationship_status",
  "children",
] as const;
export type AllowedTargetingCriteria =
  (typeof allowedTargetingCriteria)[number];

export const restrictedTargetingCriteria = [
  "mood_data",
  "emotional_state",
  "menstrual_cycle",
  "wellness_trends",
  "journal_content",
  "ai_inferred_condition",
  "health_metrics",
  "sleep_patterns",
  "stress_levels",
] as const;
export type RestrictedTargetingCriteria =
  (typeof restrictedTargetingCriteria)[number];

// ----- Studio entities -----
export interface StudioTag {
  id: string;
  name: string;
  status: StudioContentStatus;
  createdBy: string;
  createdByRole: "admin" | "coach";
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
  archivedAt: string | null;
}

export const ritualTypes = [
  "audio",
  "video",
  "breathwork",
  "affirmation",
  "meditation",
  "movement",
  "somatic",
  "journaling",
] as const;
export type RitualType = (typeof ritualTypes)[number];

export interface ContentReviewComment {
  id: string;
  contentType: StudioContentType;
  contentId: string;
  authorId: string;
  authorName: string;
  authorRole: SystemRole;
  comment: string;
  createdAt: string;
  isInternal: boolean;
}

export interface StudioContentVersion {
  id: string;
  contentType: StudioContentType;
  contentId: string;
  version: number;
  snapshot: string;
  changeNote: string | null;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
}

export interface StudioRecentItem {
  id: string;
  type: StudioContentType;
  title: string;
  status: StudioContentStatus;
  updatedAt: string;
  ownerName: string | null;
}

export interface StudioReviewQueueItem {
  id: string;
  type: StudioContentType;
  title: string;
  submittedAt: string;
  ownerName: string | null;
  ownerType: "admin" | "coach";
}

export interface ContentStudioOverview {
  blogs: { total: number; draft: number; live: number; inReview: number };
  invigorations: {
    total: number;
    draft: number;
    live: number;
    inReview: number;
  };
  videos: { total: number; draft: number; live: number; inReview: number };
  recipes: { total: number; draft: number; live: number; inReview: number };
  journeys: { total: number; draft: number; live: number; inReview: number };
  journalEntries: {
    total: number;
    draft: number;
    live: number;
    inReview: number;
  };
  recentlyUpdated: StudioRecentItem[];
  reviewQueue: StudioReviewQueueItem[];
  statusBreakdown: { status: StudioContentStatus; count: number }[];
}

export interface ContentCore {
  id: string;
  contentType: string;
  title: string;
  description: string | null;
  status: StudioContentStatus;
  version: number;

  createdBy: string;
  createdByRole: "admin" | "coach";

  createdAt: string;
  updatedAt: string;

  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  publishedAt: string | null;
  archivedAt: string | null;

  intelligence: ContentIntelligence | null;
}

// ----- Subcategory / visibility -----
export const subCategoryDisplayTypes = ["album", "single"] as const;
export type SubCategoryDisplayType = (typeof subCategoryDisplayTypes)[number];

export const visibilityPages = [
  "library",
  "home",
  "explore",
  "search",
  "journey",
] as const;
export type VisibilityPage = (typeof visibilityPages)[number];

// ----- Languages (full) -----
export const supportedLanguages = [
  "en",
  "pt",
  "es",
  "fr",
  "de",
  "it",
  "nl",
  "sv",
  "da",
  "no",
  "fi",
  "pl",
  "cs",
  "hu",
  "ro",
  "bg",
  "el",
  "tr",
  "ar",
  "he",
  "hi",
  "zh",
  "ja",
  "ko",
  "th",
  "vi",
  "id",
  "ms",
] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export interface LanguageVariant {
  id: string;
  contentId: string;
  contentType: string;
  languageCode: SupportedLanguage;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string | null;
  localizedBody: string | null;
  localizedMedia: Record<string, string>;
  localizedImages: Record<string, string>;
  translatedBy: string | null;
  translationStatus: "pending" | "in_progress" | "completed" | "reviewed";
  createdAt: string;
  updatedAt: string;
}

// ----- Placement engine -----
export const placementPageNames = ["home", "library", "explore"] as const;
export type PlacementPageName = (typeof placementPageNames)[number];

export const placementVisibilities = [
  "visible",
  "hidden",
  "scheduled",
] as const;
export type PlacementVisibility = (typeof placementVisibilities)[number];

export interface PlacementPage {
  id: string;
  name: PlacementPageName;
  slug: string;
  visibility: PlacementVisibility;
  order: number;
}

export interface PlacementCategory {
  id: string;
  pageId: string;
  title: string;
  order: number;
}

export const placementDisplayTypes = [
  "album",
  "content",
  "auto_recommended",
] as const;
export type PlacementDisplayType = (typeof placementDisplayTypes)[number];

export const placementRecommendationModes = [
  "mood_based",
  "trending",
  "personalized",
] as const;
export type PlacementRecommendationMode =
  (typeof placementRecommendationModes)[number];

export interface PlacementAutoRules {
  tagFilters: string[];
  categoryFilters: string[];
  contentTypeFilters: string[];
  recommendationMode: PlacementRecommendationMode;
}

export interface PlacementSection {
  id: string;
  categoryId: string;
  pageId: string;
  title: string;
  displayType: PlacementDisplayType;
  order: number;
  maxItems: number;
  autoRules: PlacementAutoRules | null;
}

export const placementItemTypes = [
  "album",
  "audio",
  "video",
  "blog",
  "recipe",
] as const;
export type PlacementItemType = (typeof placementItemTypes)[number];

export interface PlacementItem {
  id: string;
  sectionId: string;
  itemType: PlacementItemType;
  itemId: string;
  order: number;
}

export const resolvedPlacementSources = ["manual", "auto"] as const;
export type ResolvedPlacementSource = (typeof resolvedPlacementSources)[number];

export interface ResolvedPlacementItem {
  itemId: string;
  itemType: PlacementItemType;
  title: string;
  source: ResolvedPlacementSource;
  score: number | null;
}

export interface ResolvedPlacement {
  pageId: string;
  categoryId: string;
  sectionId: string;
  sectionTitle: string;
  displayType: PlacementDisplayType;
  items: ResolvedPlacementItem[];
}

// ----- Astrology / horoscope content -----
export interface AstrologyEntry extends StudioContentBase {
  type: "astrology";
  body: string;
  thumbnailUrl: string | null;
  heroImageUrl: string | null;

  zodiacSign: string | null;
  planetaryInfluence: string | null;
  cosmicPhase: string | null;
  validFrom: string | null;
  validTo: string | null;

  tags: string[];
}

// ----- Audio / Video content (shared shape) -----
export interface AudioContent {
  id: string;
  categoryId: string;
  subCategoryIds: string[];
  tagOptionIds: string[];
  placementOverrides: string[];
  status: StudioContentStatus;
  version: number;

  createdBy: string;
  createdByRole: "admin" | "coach";
  createdByName: string | null;

  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  archivedAt: string | null;

  primaryLanguage: string;
  internalNotes: string | null;
}

export interface AudioLanguageVariant {
  id: string;
  audioContentId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string | null;
  audioFileUrl: string | null;
  audioFileName: string | null;
  thumbnailUrl: string | null;
  durationMinutes: number | null;
  coachId: string | null;
  translationStatus: "pending" | "in_progress" | "completed" | "needs_review";
  createdAt: string;
  updatedAt: string;
}

export interface VideoContent {
  id: string;
  categoryId: string;
  subCategoryIds: string[];
  tagOptionIds: string[];
  placementOverrides: string[];
  status: StudioContentStatus;
  version: number;

  createdBy: string;
  createdByRole: "admin" | "coach";
  createdByName: string | null;

  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  archivedAt: string | null;

  primaryLanguage: string;
  internalNotes: string | null;
}

export interface VideoLanguageVariant {
  id: string;
  videoContentId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string | null;
  videoFileUrl: string | null;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  coachId: string | null;
  transcript: string | null;
  videoRatio: "LANDSCAPE" | "PORTRAIT" | null;
  translationStatus: "pending" | "in_progress" | "completed" | "needs_review";
  createdAt: string;
  updatedAt: string;
}

// ----- Workbook content variant (shared shape) -----
export interface WorkbookContentVariant {
  id: string;
  workbookContentId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedSubtitle: string | null;
  createdAt: string;
  updatedAt: string;
}

// ----- Challenge (raw, distinct from ChallengeContent) -----
export interface Challenge {
  id: string;
  categoryId: string | null;
  tagOptionIds: string[];
  status: StudioContentStatus;
  version: number;
  baseLanguage: string;

  createdBy: string;
  createdByRole: "admin" | "coach";
  createdByName: string | null;

  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
}

export interface ChallengeVariant {
  id: string;
  challengeId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  audioFileUrl: string | null;
  audioFileName: string | null;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
  coachId: string | null;
  createdAt: string;
  updatedAt: string;
}

// ----- Ritual composer -----
export interface ReflectionLanguageVariant {
  languageCode: string;
  title: string;
  subtitle: string;
  placeholder: string;
}

export interface AffirmationBlock {
  enabled: boolean;
  affirmationIds: string[];
}

export interface MindfulTransitionBlock {
  enabled: boolean;
  sourceType: "audio" | "album";
  sourceIds: string[];
}

export interface ReflectionBlock {
  enabled: boolean;
  languageVariants: ReflectionLanguageVariant[];
}

export interface AstrologyBlock {
  enabled: boolean;
}

export interface RitualTemplate {
  id: string;
  moodTagOptionIds: string[];
  status: StudioContentStatus;
  version: number;

  createdBy: string;
  createdByRole: "admin" | "coach";
  createdByName: string | null;

  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  archivedAt: string | null;

  internalNotes: string | null;
  priority: number;

  affirmationBlock: AffirmationBlock;
  mindfulTransitionBlock: MindfulTransitionBlock;
  reflectionBlock: ReflectionBlock;
  astrologyBlock: AstrologyBlock;
}

// ----- Ritual delivery -----
export const ritualFallbackModes = [
  "generic",
  "latest",
  "highest_priority",
] as const;
export type RitualFallbackMode = (typeof ritualFallbackModes)[number];

export interface RitualServingConfig {
  id: string;
  rotationEnabled: boolean;
  avoidRecentCount: number;
  fallbackMode: RitualFallbackMode;
  enablePrioritySorting: boolean;
  updatedAt: string;
}

export interface RitualServingHistory {
  id: string;
  ritualTemplateId: string;
  appUserId: string;
  servedAt: string;
}

export interface RitualServeRequest {
  appUserId: string;
  mood: string;
}

// ----- Recommendation engine -----
export const recommendationRuleTypes = [
  "mood",
  "fallback",
  "priority",
] as const;
export type RecommendationRuleType = (typeof recommendationRuleTypes)[number];

export const recommendationContentTypes = [
  "audio",
  "video",
  "blog",
  "recipe",
] as const;
export type RecommendationContentType =
  (typeof recommendationContentTypes)[number];

export interface RecommendationRule {
  id: string;
  type: RecommendationRuleType;
  name: string;
  moodTagOptionId: string | null;
  preferredCategoryIds: string[];
  preferredContentTypes: string[];
  preferredTagOptionIds: string[];
  boostContentIds: string[];
  boostLevel: number;
  fallbackOrder: number;
  enabled: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ----- Zodiac / horoscope -----
export const zodiacSigns = [
  "aries",
  "taurus",
  "gemini",
  "cancer",
  "leo",
  "virgo",
  "libra",
  "scorpio",
  "sagittarius",
  "capricorn",
  "aquarius",
  "pisces",
] as const;
export type ZodiacSign = (typeof zodiacSigns)[number];

export interface HoroscopeEntry {
  id: string;
  sign: ZodiacSign;
  date: string;
  horoscopeText: string;
  languageCode: string;
}

export interface ReflectionPrompt {
  promptText: string;
  journalFieldLabel: string | null;
}

export interface ReflectionPromptSet {
  id: string;
  name: string;
  moodTagOptionId: string | null;
  isDefault: boolean;
  prompts: ReflectionPrompt[];
  languageCode: string;
  createdAt: string;
}

export interface GeneratedRitual {
  id: string;
  userId: string;
  date: string;
  timezone: string;
  moodTagOptionId: string | null;
  affirmationContentId: string | null;
  audioContentId: string | null;
  reflectionPromptSetId: string | null;
  horoscopeId: string | null;
  languageCode: string;
  generatedAt: string;
  status: "complete" | "partial" | "failed";
  missingBlocks: string[];
}

export interface RitualAutomationConfig {
  id: string;
  oracleAvoidRepeatDays: number;
  affirmationAvoidRepeatDays: number;
  audioAvoidRepeatDays: number;
  defaultFallbackMood: string;
  enabled: boolean;
  updatedAt: string;
}

export interface RitualAssemblyRequest {
  userId: string;
  date: string;
  timezone: string;
  moodTagOptionId?: string | null;
  languageCode: string;
  zodiacSign?: ZodiacSign;
}

// ----- Recommendation / intelligence engine -----
export const recommendationSources = [
  "mood",
  "profile",
  "placement",
  "trending",
] as const;
export type RecommendationSource = (typeof recommendationSources)[number];

export const intelligenceContentTypes = [
  "audio",
  "video",
  "blog",
  "recipe",
  "workbook",
  "journey",
  "affirmation",
] as const;
export type IntelligenceContentType = (typeof intelligenceContentTypes)[number];

export interface UserSignals {
  moodTagId: string | null;
  selectedFeelingTagIds: string[];
  energyLevelTagId: string | null;
  concentrationTagId: string | null;
  contentLengthTagId: string | null;
  cyclePhaseTagId: string | null;
  preferredLanguage: string;
  consumedContentIds: string[];
  savedContentIds: string[];
}

export type ValidatedUserSignals = UserSignals;

export interface RecommendedContentResult {
  contentId: string;
  contentType: IntelligenceContentType;
  title: string;
  score: number;
  matchedSignals: string[];
  languageVariantUsed: string;
  source: RecommendationSource;
}

// ----- Reflection template builder -----
export const reflectionFieldTypes = [
  "text",
  "textarea",
  "emotion_scale",
  "number",
] as const;
export type ReflectionFieldType = (typeof reflectionFieldTypes)[number];

export interface ReflectionTemplateFieldVariant {
  languageCode: string;
  localizedLabel: string;
}

export interface ReflectionTemplateField {
  id: string;
  label: string;
  fieldType: ReflectionFieldType;
  required: boolean;
  order: number;
  languageVariants: ReflectionTemplateFieldVariant[];
}

export interface ReflectionTemplateStepVariant {
  languageCode: string;
  localizedTitle: string;
  localizedSubtitle: string;
}

export interface ReflectionTemplateStep {
  id: string;
  order: number;
  title: string;
  subtitle: string;
  fields: ReflectionTemplateField[];
  languageVariants: ReflectionTemplateStepVariant[];
}

export interface ReflectionTemplate {
  id: string;
  status: StudioContentStatus;
  version: number;
  moodTagOptionIds: string[];
  baseLanguage: string;
  steps: ReflectionTemplateStep[];

  createdBy: string;
  createdByRole: "admin" | "coach";
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  internalNotes: string | null;
}

// ----- Mood mapping -----
export interface MoodContentMap {
  id: string;
  moodTagOptionId: string;
  affirmationIds: string[];
  audioIds: string[];
  reflectionTemplateIds: string[];
  updatedAt: string;
  updatedBy: string;
}

// ----- Daily ritual instance -----
export const dailyRitualCompletionStates = [
  "pending",
  "in_progress",
  "completed",
] as const;
export type DailyRitualCompletionState =
  (typeof dailyRitualCompletionStates)[number];

export interface DailyRitualInstance {
  id: string;
  userId: string;
  date: string;
  mood: string;
  affirmationId: string | null;
  audioId: string | null;
  reflectionTemplateId: string | null;
  horoscopeId: string | null;
  completionState: DailyRitualCompletionState;
  createdAt: string;
}

// ----- Horoscope sign -----
export interface HoroscopeSign {
  id: string;
  name: string;
  slug: ZodiacSign;
  symbol: string;
  element: "fire" | "earth" | "air" | "water";
  rulingPlanet: string;
  dateRangeStart: string;
  dateRangeEnd: string;
  dateRangeLabel: string;
  iconUrl: string | null;
  backgroundImageUrl: string | null;
  gradientTheme: string | null;
  colorHex: string | null;
  displayOrder: number;
  status: StudioContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ZodiacSignConfig {
  sign: ZodiacSign;
  symbol: string;
  element: "fire" | "earth" | "air" | "water";
  rulingPlanet: string;
  dateRange: string;
  iconUrl: string | null;
  colorHex: string | null;
}

// ----- Astrology batches -----
export interface AstrologyBatch {
  id: string;
  year: number;
  label: string | null;
  entryCount: number;
  status: "pending" | "completed" | "failed";
  uploadedBy: string;
  uploadedByName: string | null;
  createdAt: string;
}

export interface AstrologyContentEntry {
  id: string;
  zodiacSign: ZodiacSign;
  date: string;
  categoryId: string | null;
  tagOptionIds: string[];
  batchId: string | null;
  status: StudioContentStatus;
  version: number;

  createdBy: string;
  createdByRole: "admin" | "coach";
  createdByName: string | null;

  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  archivedAt: string | null;

  primaryLanguage: string;
  internalNotes: string | null;
}

export interface AstrologyLanguageVariant {
  id: string;
  astrologyEntryId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string | null;
  localizedReadingText: string;
  thumbnailUrl: string | null;
  translationStatus: "pending" | "in_progress" | "completed" | "needs_review";
  createdAt: string;
  updatedAt: string;
}

export type Phase17AstrologyEntry = AstrologyContentEntry & {
  variants: AstrologyLanguageVariant[];
  baseLanguage: string;
};
export type Phase17AstrologyVariant = AstrologyLanguageVariant;

// ----- Multi-language taxonomy variants -----
export interface CategoryLanguageVariant {
  id: string;
  parentEntityId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string | null;
  status: StudioContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TagGroupLanguageVariant {
  id: string;
  parentEntityId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string | null;
  status: StudioContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TagOptionLanguageVariant {
  id: string;
  parentEntityId: string;
  languageCode: string;
  isDefault: boolean;
  localizedLabel: string;
  localizedDescription: string | null;
  status: StudioContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface SubCategoryLanguageVariant {
  id: string;
  parentEntityId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string | null;
  status: StudioContentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AlbumLanguageVariant {
  id: string;
  parentEntityId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string | null;
  status: StudioContentStatus;
  createdAt: string;
  updatedAt: string;
}

// =====================================================================
// END merged block
// =====================================================================

// ----- Legacy content (used by older content-library / review-queue / content-card) -----
export const contentTypes = [
  "blog",
  "invigoration",
  "video",
  "recipe",
  "journey",
  "journal_entry",
  "audio",
  "affirmation",
  "guided_audio",
  "workbook_question",
  "quote",
  "challenge",
  "workbook",
  "horoscope",
  "album",
  "breathwork",
] as const;
export type ContentType = (typeof contentTypes)[number];

export interface Content {
  id: string;
  title: string;
  type: ContentType;
  description: string | null;
  body: string | null;
  mainPhotoUrl: string | null;
  coachId: string | null;
  coachName: string | null;
  category: string | null;
  visibleInApp: boolean;
  isFeatured: boolean;
  appSurfaces: string[] | null;
  homeDisplayOrder: number | null;
  categoryIds: string[] | null;
  audioUrl: string | null;
  videoUrl: string | null;
  duration: number | null;
  tags: string[] | null;
  thumbnailUrl: string | null;
  mediaUrl: string | null;
  totalDays: number | null;
  journeyDays: string | null;
  journeyDay?: number | null;
  journalType: string | null;
  journalQuestions: string | null;
  status: ContentStatus;
  priority: number;
  createdBy: string | null;
  createdByRole: string | null;
  createdAt: string | null;
  lastModified: string | null;
  lastModifiedBy: string | null;
  submittedAt: string | null;
  submittedBy: string | null;
  reviewedBy: string | null;
  reviewedByUsername: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  scheduledPublish: string | null;
  archivedAt: string | null;
  archivedBy: string | null;
  version: number;
  previousVersionId: string | null;
}

// ----- GDPR -----
export const gdprDeletionReasons = [
  "user_request",
  "legal_obligation",
  "legal_compliance",
  "policy_violation",
  "duplicate_account",
  "gdpr_right_to_forget",
  "other",
] as const;
export type GdprDeletionReason = (typeof gdprDeletionReasons)[number];

export interface GdprExportRequest {
  id: string;
  appUserId: string;
  userId: string;
  userName: string | null;
  maskedEmail: string | null;
  status:
    | "pending"
    | "processing"
    | "ready"
    | "downloaded"
    | "expired"
    | "failed"
    | "completed";
  reason: string;
  requestedBy: string;
  requestedByUsername: string;
  requestedAt: string;
  completedAt: string | null;
  downloadUrl: string | null;
  expiresAt: string | null;
  downloadCount?: number;
  maxDownloads?: number;
  downloadExpiry?: string | null;
}

export interface GdprDeletionRequest {
  id: string;
  appUserId: string;
  userId: string;
  userName: string | null;
  maskedEmail: string | null;
  status:
    | "pending_review"
    | "pending_approval"
    | "approved"
    | "rejected"
    | "completed"
    | "cancelled";
  reason: GdprDeletionReason;
  reasonDetails: string;
  requestedBy: string;
  requestedByUsername: string;
  requestedAt: string;
  reviewedBy: string | null;
  reviewedByUsername: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  completedAt: string | null;
}

export const appSurfaces = [
  "home",
  "library",
  "category_page",
  "journey",
  "blog_section",
  "recipe_section",
  "journal_section",
] as const;
export type AppSurface = (typeof appSurfaces)[number];

export const contentTypeSurfaceMapping: Record<string, AppSurface[]> = {
  blog: ["blog_section", "category_page", "home"],
  invigoration: ["library", "category_page", "journey", "home"],
  video: ["library", "category_page", "journey", "home"],
  recipe: ["recipe_section", "category_page", "home"],
  journey: ["library", "category_page", "home"],
  journal_entry: ["journey", "journal_section"],
};
